import type { Company } from "@prisma/client";
import Text from "~/ui/Text";
import type { MetaFunction } from "@remix-run/node";
import type { ActionFunction, LoaderFunction } from "@remix-run/server-runtime";
import { json } from "@remix-run/server-runtime";
import title from "~/kernel/meta/title";
import validateCreateReview from "~/routes/companies.$slug.reviews.new/validateCreateReview";
import ROUTES from "~/kernel/routing/ROUTES";
import Card from "~/ui/Card";
import Heading from "~/ui/Heading";
import SpacedContent from "~/ui/SpacedContent";
import type ValidationErrors from "~/kernel/validation/ValidationErrors";
import ReviewForm from "./ReviewForm";
import redirect from "~/kernel/server/redirect";
import Container from "~/ui/Container";
import requireResource from "~/kernel/server/requireResource";
import companyRepository from "~/repositories/companyRepository";
import translationHandle from "~/kernel/internationalization/translationHandle";
import getFixedTranslation from "~/kernel/internationalization/getFixedTranslation.server";
import createReview from "~/routes/companies.$slug.reviews.new/createReview/createReview";
import useTranslation from "~/kernel/internationalization/useTranslation";
import isNil from "~/kernel/validation/isNil";
import isPresent from "~/kernel/validation/isPresent";
import { NewReviewBreadcrumb } from "./NewReviewBreadcrumb";
import { showError } from "./showError";
import { deserializeReview } from "./deserializeReview";
import getUser from "~/kernel/auth/getUser.server";
import { useActionData, useLoaderData } from "@remix-run/react";
import LinkButton from "~/ui/LinkButton";
import customerRepository from "~/repositories/customerRepository";
import { reviewPolicy } from "~/policies/reviewPolicy";
import type { User } from "~/models/user/User";
import { findReviewableBooking } from "~/review/helpers/findReviewableBooking";

export const meta: MetaFunction<typeof loader> = ({ data }) => [
  { title: title(data.pageTitle) },
];

export const handle = translationHandle({
  ns: { reviews: true },
});

export type LoaderData =
  | {
      company: Company;
      pageTitle: string;
      isSuccessful: true;
    }
  | {
      isSuccessful: false;
      company: Company;
      pageTitle: string;
      code: "NOT_SIGNED_IN";
    }
  | {
      isSuccessful: false;
      company: Company;
      pageTitle: string;
      code: "NOT_A_CUSTOMER";
    }
  | {
      isSuccessful: false;
      company: Company;
      pageTitle: string;
      code: "BOOKING_NOT_REVIEWABLE";
    };

export const loader: LoaderFunction = async ({ request, params }) => {
  const translation = await getFixedTranslation(request, handle);
  const { t } = translation;
  const slug = params.slug;
  requireResource(slug, { errorMessage: t("new.companyNotFound") });
  const user = await getUser(request);
  const pageTitle = t("new.pageTitle");
  const company = await companyRepository.findBySlug(slug);
  requireResource(company, { errorMessage: t("new.companyNotFound") });

  if (isNil(user)) {
    return json<LoaderData>({
      code: "NOT_SIGNED_IN",
      isSuccessful: false,
      pageTitle,
      company,
    });
  }

  const customer = await customerRepository.findByUserAndCompany({
    userId: user.id,
    companySlug: company.slug,
  });

  if (!reviewPolicy.canReview(user, customer, company)) {
    return json<LoaderData>({
      code: "NOT_A_CUSTOMER",
      isSuccessful: false,
      pageTitle,
      company,
    });
  }

  const bookingId = ROUTES.newReview.parseQuery(request)?.bookingId;

  if (!isNil(bookingId) && !(await isReviewable(bookingId, user, company))) {
    return json<LoaderData>({
      code: "BOOKING_NOT_REVIEWABLE",
      isSuccessful: false,
      pageTitle,
      company,
    });
  }

  return json<LoaderData>({
    company,
    pageTitle,
    isSuccessful: true,
  });
};

async function isReviewable(
  bookingId: string,
  user: User,
  company: Company,
): Promise<boolean> {
  const booking = await findReviewableBooking({
    userId: user.id,
    companyId: company.id,
    bookingId,
  });

  return isPresent(booking);
}

export type ActionData =
  | {
      isSuccessful: true;
      successMessage?: string;
      errorMessage?: string;
      errors?: ValidationErrors;
    }
  | {
      successMessage?: string;
      isSuccessful: false;
      errorMessage?: string;
      code: "INVALID_DATA";
      errors?: ValidationErrors;
    }
  | {
      isSuccessful: false;
      errorMessage?: string;
      code: "NOT_SIGNED_IN";
      errors?: ValidationErrors;
    }
  | {
      isSuccessful: false;
      code: "NOT_A_CUSTOMER";
      errorMessage?: string;
      errors?: ValidationErrors;
    }
  | {
      isSuccessful: false;
      code: "BOOKING_NOT_REVIEWABLE";
      errorMessage?: string;
      errors?: ValidationErrors;
    };

export const action: ActionFunction = async ({ request, params }) => {
  const translation = await getFixedTranslation(request, handle);
  const { t } = translation;
  const slug = params.slug;
  requireResource(slug, { errorMessage: t("new.companyNotFound") });
  const formData = await request.formData();
  const successMessage = "Tack för din recension!";
  const unvalidatedData = deserializeReview(formData);
  const company = await companyRepository.findBySlug(slug);
  requireResource(company);
  const user = await getUser(request);

  if (isNil(user)) {
    return json<ActionData>({
      isSuccessful: false,
      code: "NOT_SIGNED_IN",
    });
  }

  const customer = await customerRepository.findByUserAndCompany({
    userId: user.id,
    companySlug: company.slug,
  });

  if (!reviewPolicy.canReview(user, customer, company)) {
    return json<ActionData>({
      isSuccessful: false,
      code: "NOT_A_CUSTOMER",
    });
  }

  if (isNil(company)) {
    return redirect(request, ROUTES.companyNotFound.path({ slug }));
  }

  if (!unvalidatedData.isPermitted) {
    return json<ActionData>({
      successMessage,
      isSuccessful: false,
      code: "INVALID_DATA",
    });
  }

  const validationResult = validateCreateReview(
    { ...unvalidatedData, userId: user.id },
    translation,
  );

  if (!unvalidatedData.acceptsTermsOfUsage) {
    return json<ActionData>({
      isSuccessful: true,
      errorMessage: t("new.couldNotSubmit"),
      errors: {
        acceptsTermsOfUsage: t("new.mustAcceptTermsOfUsage"),
      },
    });
  }

  if (!validationResult.isValid) {
    const errorMessage = t("new.couldNotSubmit");
    const { errors } = validationResult;
    return json<ActionData>({
      errorMessage,
      errors,
      isSuccessful: false,
      code: "INVALID_DATA",
    });
  }

  const data = validationResult.data;
  const bookingId = ROUTES.newReview.parseQuery(request)?.bookingId;
  const booking = isNil(bookingId)
    ? null
    : await findReviewableBooking({
        userId: user.id,
        companyId: company.id,
        bookingId,
      });

  if (isPresent(bookingId) && isNil(booking)) {
    return json<ActionData>({
      isSuccessful: false,
      code: "BOOKING_NOT_REVIEWABLE",
    });
  }

  return createReview(
    {
      user: { connect: { id: user.id } },
      score: data.score,
      reviewText: data.reviewText,
      reviewerName: user.name,
      booking: booking ? { connect: { id: booking.id } } : undefined,
      company: { connect: { id: company.id } },
    },
    company,
    translation,
  )
    .then(() => {
      return redirect(
        request,
        ROUTES.company.path({ params: { slug: company.slug } }),
        {
          flashMessage: {
            type: "success",
            message: t("new.successMessage"),
          },
        },
      );
    })
    .catch((error) => showError(error, validationResult.data));
};

function useErrorCode() {
  const loaderData = useLoaderData<LoaderData>();
  const actionData = useActionData<ActionData>();

  if (actionData && !actionData.isSuccessful) {
    return actionData.code;
  }

  if (loaderData.isSuccessful) {
    return undefined;
  }

  return loaderData.code;
}

export default function NewReviewPage() {
  const { getScopedT } = useTranslation();
  const loaderData = useLoaderData<LoaderData>();
  const scopedT = getScopedT("new");
  const errorCode = useErrorCode();

  if (errorCode && errorCode !== "INVALID_DATA") {
    switch (errorCode) {
      case "BOOKING_NOT_REVIEWABLE":
      case "NOT_A_CUSTOMER":
        return (
          <Container size="small">
            <div className="space-y-2">
              <NewReviewBreadcrumb />
              <Card>
                <SpacedContent>
                  <Heading>{scopedT("canNotAddReview")}</Heading>
                  <Text>{scopedT(`errors.${errorCode}`)}</Text>
                </SpacedContent>
              </Card>
            </div>
          </Container>
        );
      case "NOT_SIGNED_IN":
      default:
        return (
          <Container size="small">
            <div className="space-y-2">
              <NewReviewBreadcrumb />
              <Card>
                <SpacedContent>
                  <Heading>{scopedT("canNotAddReview")}</Heading>
                  <Text>{scopedT(`errors.${errorCode}`)}</Text>
                  <LinkButton
                    fullWidth={false}
                    to={ROUTES.login.path({
                      redirectUrl: ROUTES.newReview.path({
                        params: { slug: loaderData.company.slug },
                      }),
                    })}
                  >
                    {scopedT("signIn")}
                  </LinkButton>
                </SpacedContent>
              </Card>
            </div>
          </Container>
        );
    }
  }

  return (
    <Container size="small">
      <div className="space-y-2">
        <NewReviewBreadcrumb />
        <Card>
          <SpacedContent>
            <Heading>{scopedT("pageTitle")}</Heading>
            <ReviewForm />
          </SpacedContent>
        </Card>
      </div>
    </Container>
  );
}
